import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Wrench, RefreshCw, MessageCircle, Loader2, Volume2, VolumeX } from "lucide-react";

const SUPORTE = "https://wa.me/5585988203236";
const PADRAO = "Estamos fazendo melhorias na plataforma. Volte em instantes!";
// áreas que ficam bloqueadas para aluna e professora; o site público e os links de venda continuam no ar
const AREAS = ["/app", "/prof", "/primeiro-acesso", "/tricolab"];

/** Estado da manutenção: busca ao abrir, a cada 60 s e quando o servidor responde "MANUTENCAO". */
export function useManutencao() {
  const [m, setM] = useState({ ativo: false, mensagem: PADRAO, previsao: "" });
  const carregar = useCallback(() => api.get("/public/manutencao").then((r) => setM(r.data)).catch(() => {}), []);
  useEffect(() => {
    carregar();
    const t = setInterval(carregar, 60000);
    window.addEventListener("manutencao", carregar);
    return () => { clearInterval(t); window.removeEventListener("manutencao", carregar); };
  }, [carregar]);
  return [m, carregar];
}

/** Música da tela de manutenção: tenta tocar ao abrir; se o navegador bloquear, começa no primeiro toque na tela. */
function MusicaManutencao() {
  const ref = useRef(null);
  const [tocando, setTocando] = useState(false);
  useEffect(() => {
    const a = ref.current;
    if (!a) return undefined;
    a.volume = 0.35;
    const tocar = () => a.play().then(() => setTocando(true)).catch(() => {});
    tocar();
    const noToque = () => { if (a.paused) tocar(); };
    window.addEventListener("pointerdown", noToque, { once: true });
    window.addEventListener("keydown", noToque, { once: true });
    return () => { window.removeEventListener("pointerdown", noToque); window.removeEventListener("keydown", noToque); a.pause(); };
  }, []);
  const alternar = (e) => {
    e.stopPropagation();
    const a = ref.current;
    if (a.paused) a.play().then(() => setTocando(true)).catch(() => {}); else { a.pause(); setTocando(false); }
  };
  return (
    <>
      <audio ref={ref} src="/sons/manutencao.mp3" loop preload="auto" />
      <button onClick={alternar} className="fixed bottom-5 right-5 inline-flex items-center gap-2 rounded-full bg-white/90 px-4 py-2 text-sm font-semibold text-[#232D4F] shadow-lg hover:bg-white" data-testid="musica-manutencao">
        {tocando ? <><Volume2 className="h-4 w-4" /> Pausar música</> : <><VolumeX className="h-4 w-4" /> 🔊 Tocar música</>}
      </button>
    </>
  );
}

export function TelaManutencao({ m, onTentar }) {
  return (
    <div className="min-h-screen grid place-items-center px-5" style={{ background: "linear-gradient(135deg,#1A2340,#232D4F 55%,#1E9E2A)" }} data-testid="tela-manutencao">
      <div className="max-w-lg w-full bg-white rounded-2xl shadow-2xl p-8 text-center">
        <img src="/brand/app-192.png" alt="IEP" className="h-20 w-20 mx-auto" />
        <div className="mt-4 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#B45309] bg-amber-50 border border-amber-200 rounded-full px-3 py-1">
          <Wrench className="h-3.5 w-3.5" /> Em manutenção
        </div>
        <h1 className="font-display text-2xl font-bold text-slate-900 mt-4">Voltamos já! 🛠️</h1>
        <p className="text-slate-600 mt-3 whitespace-pre-line">{m.mensagem || PADRAO}</p>
        {m.previsao && <p className="mt-3 text-sm text-slate-700">Previsão de volta: <strong>{m.previsao}</strong></p>}
        <p className="mt-3 text-sm text-slate-500">Seus cursos, seu progresso e seus certificados estão guardados.</p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <Button onClick={onTentar} className="text-white" style={{ background: "#1E9E2A" }}><RefreshCw className="h-4 w-4 mr-1.5" /> Tentar de novo</Button>
          <a href={SUPORTE} target="_blank" rel="noopener" className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"><MessageCircle className="h-4 w-4" /> Falar com o suporte</a>
        </div>
      </div>
      <MusicaManutencao />
    </div>
  );
}

/** Mostra a tela de manutenção para aluna e professora nas áreas internas; o admin (e o admin vendo como aluna) passa. */
export function ManutencaoGate({ children }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [m, carregar] = useManutencao();
  const area = AREAS.some((a) => pathname === a || pathname.startsWith(a + "/"));
  const logadaComum = user && user.role !== "admin" && !user.impersonator;
  if (m.ativo && (area || (logadaComum && pathname === "/")) && !(user && (user.role === "admin" || user.impersonator)))
    return <TelaManutencao m={m} onTentar={() => { carregar(); window.location.reload(); }} />;
  return children;
}

/** Botão do menu do admin: liga e desliga a manutenção. */
export function BotaoManutencao() {
  const [m, carregar] = useManutencao();
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState(PADRAO);
  const [prev, setPrev] = useState("");
  const [busy, setBusy] = useState(false);
  const abrir = () => { setMsg(m.mensagem || PADRAO); setPrev(m.previsao || ""); setOpen(true); };
  const salvar = async (ativo) => {
    setBusy(true);
    try {
      await api.put("/admin/manutencao", { ativo, mensagem: msg, previsao: prev });
      await carregar(); setOpen(false);
      toast.success(ativo ? "Plataforma em manutenção. Alunas e professoras veem o aviso." : "Manutenção encerrada. A plataforma voltou para todas.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <>
      <button onClick={abrir} data-testid="admin-nav-manutencao"
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors mt-2 border ${m.ativo ? "bg-red-600 text-white border-red-500 hover:bg-red-700 animate-pulse" : "text-white/80 border-white/15 hover:bg-white/10"}`}>
        <Wrench className="h-5 w-5" /> {m.ativo ? "EM MANUTENÇÃO" : "Manutenção"}
        <span className={`ml-auto h-2.5 w-2.5 rounded-full ${m.ativo ? "bg-white" : "bg-green-400"}`} />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Wrench className="h-5 w-5" /> Modo manutenção</DialogTitle>
            <DialogDescription>
              {m.ativo ? "A plataforma está EM MANUTENÇÃO: alunas e professoras veem o aviso abaixo." : "Ao ligar, alunas e professoras veem o aviso abaixo e não conseguem usar a área delas. Você (admin) continua usando tudo. O site público e os links de venda continuam no ar."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><label className="text-xs text-slate-500">Mensagem para as alunas</label>
              <Textarea value={msg} maxLength={500} rows={3} onChange={(e) => setMsg(e.target.value)} /></div>
            <div className="space-y-1"><label className="text-xs text-slate-500">Previsão de volta (opcional)</label>
              <Input value={prev} maxLength={80} placeholder="Ex.: hoje às 22h" onChange={(e) => setPrev(e.target.value)} /></div>
          </div>
          <div className="flex flex-wrap gap-2 justify-end pt-2">
            {m.ativo ? (<>
              <Button variant="outline" disabled={busy} onClick={() => salvar(true)}>Atualizar aviso</Button>
              <Button disabled={busy} onClick={() => salvar(false)} className="text-white" style={{ background: "#1E9E2A" }}>{busy && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}Tirar da manutenção</Button>
            </>) : (
              <Button disabled={busy} onClick={() => salvar(true)} className="text-white bg-red-600 hover:bg-red-700">{busy && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}Colocar em manutenção</Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** Faixa no topo do admin enquanto a manutenção está ligada (para não esquecer ligada). */
export function FaixaManutencaoAdmin() {
  const [m] = useManutencao();
  if (!m.ativo) return null;
  return <div className="bg-red-600 text-white text-sm font-semibold text-center py-1.5 px-4"><Wrench className="inline h-4 w-4 -mt-0.5 mr-1" /> Plataforma EM MANUTENÇÃO: alunas e professoras estão vendo o aviso. Para liberar, use o botão "Manutenção" no menu.</div>;
}
